import { useEffect, useRef, useState } from "react";
import { api, fmtNumber } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { FlaskConical, RotateCcw, Save, Info, Loader2, TrendingUp } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Legend,
} from "recharts";
import { toast } from "sonner";

const CURVE_DATA = Array.from({ length: 21 }, (_, i) => {
  const consumed = i * 5;
  return {
    consumed,
    quadratic: Math.round((1 + 4 * Math.pow(consumed / 100, 2)) * 100) / 100,
    linear: Math.round((1 + 4 * (consumed / 100)) * 100) / 100,
  };
});

const FACTOR_ORDER = ["condition", "risk", "remaining_life", "utilisation", "environment", "cost"];

export function MethodologyTab() {
  const { role } = usePermissions();
  const isExec = (role || "").toLowerCase() === "executive";
  const [cfg, setCfg] = useState(null);
  const [weights, setWeights] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const previewSeq = useRef(0);

  const load = async () => {
    const r = await api.get("/capital/methodology");
    setCfg(r.data);
    setWeights(r.data.weights);
  };
  useEffect(() => { load().catch(() => toast.error("Failed to load methodology")); }, []);

  const runPreview = (w) => {
    if (!isExec) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const seq = ++previewSeq.current;
      setPreviewing(true);
      try {
        const r = await api.post("/capital/methodology/preview", { weights: w }, { timeout: 60000 });
        if (seq === previewSeq.current) setPreview(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Preview failed");
      } finally { if (seq === previewSeq.current) setPreviewing(false); }
    }, 500);
  };

  const setW = (key, val) => {
    const w = { ...weights, [key]: val };
    setWeights(w);
    runPreview(w);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/capital/methodology", { weights }, { timeout: 120000 });
      toast.success(`Custom methodology applied — ${fmtNumber(r.data.reranked)} assets re-ranked`);
      await load();
      setPreview(null);
      window.dispatchEvent(new Event("gbe:methodology-changed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply methodology");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.post("/capital/methodology/reset");
      toast.success("Reverted to default ranking");
      await load();
      setPreview(null);
      window.dispatchEvent(new Event("gbe:methodology-changed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset");
    } finally { setSaving(false); }
  };

  if (!cfg || !weights) return <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />;

  const totalWeight = FACTOR_ORDER.reduce((s, k) => s + (weights[k] || 0), 0);

  return (
    <div className="space-y-5" data-testid="capital-methodology-tab">
      {/* Header / status */}
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center gap-3">
          <FlaskConical className="w-5 h-5 text-gbe" />
          <h2 className="font-display text-lg font-semibold text-slate-900">Ranking Methodology</h2>
          {cfg.active ? (
            <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100" data-testid="methodology-status-badge">Custom formula active</Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100" data-testid="methodology-status-badge">Default values</Badge>
          )}
          <span className="ml-auto text-xs text-slate-500">{fmtNumber(cfg.scored_assets)} scored assets</span>
        </div>
        <p className="text-sm text-slate-600 mt-3 max-w-3xl">{cfg.formula}</p>
        {cfg.updated_by && (
          <p className="text-[11px] text-slate-400 mt-1">Last changed by {cfg.updated_by} · {(cfg.updated_at || "").slice(0, 16).replace("T", " ")}</p>
        )}
      </div>

      {/* Pipeline explanation */}
      <div className="grid md:grid-cols-5 gap-3">
        {cfg.pipeline.map((p) => (
          <div key={p.step} className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-wider text-gbe font-semibold">{p.step}</div>
            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{p.detail}</p>
          </div>
        ))}
      </div>

      {/* Life-pressure degradation curve */}
      <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="methodology-degradation-curve">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-gbe" />
          <h3 className="font-display text-base font-semibold text-slate-900">Life-pressure degradation curve</h3>
          <span className="ml-auto font-mono text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-0.5">
            pressure = 1 + 4 × (age ÷ expected life)²
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-1 max-w-3xl">
          Assets don't wear linearly — degradation accelerates as they approach end of life. The quadratic derating curve keeps
          life pressure low through early and mid life, then ramps sharply in the final third. Example: at 50% of expected
          life the pressure is only 2.0 / 5, but at 90% it reaches 4.2 / 5.
        </p>
        <div className="h-56 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={CURVE_DATA} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
              <XAxis dataKey="consumed" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
              <YAxis domain={[1, 5]} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v, n) => [v, n === "quadratic" ? "Quadratic (used)" : "Linear (reference)"]}
                       labelFormatter={(v) => `${v}% of life consumed`} />
              <Legend formatter={(v) => (v === "quadratic" ? "Quadratic derating (used)" : "Linear (reference)")} wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine x={80} stroke="#e11d48" strokeDasharray="4 3"
                             label={{ value: "accelerating wear", fontSize: 9, fill: "#e11d48", position: "insideTopRight" }} />
              <Line type="monotone" dataKey="quadratic" stroke="#0f4c75" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="linear" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Weights editor */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">Factor weights</span>
          <span className="text-[11px] text-slate-400">total {Math.round(totalWeight)}</span>
          {!isExec && (
            <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-500">
              <Info className="w-3.5 h-3.5" /> Read-only — only executives can change weights
            </span>
          )}
          {isExec && (
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="outline" className="h-8" onClick={reset} disabled={saving} data-testid="methodology-reset-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to default values
              </Button>
              <Button size="sm" className="h-8 bg-gbe hover:bg-gbe-hover text-white" onClick={save} disabled={saving} data-testid="methodology-save-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                Apply & re-rank portfolio
              </Button>
            </div>
          )}
        </div>
        <div className="divide-y divide-slate-100">
          {cfg.factor_docs.map((f) => (
            <div key={f.key} className="px-5 py-3 grid md:grid-cols-[180px_1fr_260px_70px] gap-3 items-center">
              <div>
                <div className="text-sm font-medium text-slate-900">{f.label}</div>
                <div className="text-[10px] text-slate-400">{f.key}</div>
              </div>
              <div className="text-xs text-slate-500">
                <div>{f.source}</div>
                <div className="text-[10px] text-slate-400 mt-0.5 font-mono">{f.normalise}</div>
              </div>
              <Slider value={[weights[f.key] ?? 0]} min={0} max={50} step={1}
                      disabled={!isExec}
                      onValueChange={([v]) => setW(f.key, v)}
                      data-testid={`methodology-slider-${f.key}`} />
              <div className="text-right">
                <span className="text-sm font-semibold text-slate-900 tabular-nums">{weights[f.key] ?? 0}</span>
                <span className="text-[10px] text-slate-400 ml-1">
                  {totalWeight > 0 ? `${Math.round(((weights[f.key] || 0) / totalWeight) * 100)}%` : "0%"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Live preview */}
      {isExec && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">Live preview — top 20 re-ranked assets</span>
            {previewing && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
            {preview && (
              <span className="ml-auto text-[11px] text-slate-500">
                Beyond Life {preview.status_counts["Immediate Attention"]} · 12M {preview.status_counts["12 Month Plan"]} ·
                24M {preview.status_counts["24 Month Plan"]} · Monitor {preview.status_counts["Monitor"]}
              </span>
            )}
          </div>
          {!preview ? (
            <div className="p-6 text-sm text-slate-500 text-center">Move any slider to preview the re-ranked portfolio (nothing is saved until you click "Apply").</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="methodology-preview-table">
                <thead>
                  <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="text-left font-medium px-3 py-2">New #</th>
                    <th className="text-left font-medium px-3 py-2">Was #</th>
                    <th className="text-left font-medium px-3 py-2">Asset</th>
                    <th className="text-left font-medium px-3 py-2">Site</th>
                    <th className="text-right font-medium px-3 py-2">New score</th>
                    <th className="text-right font-medium px-3 py-2">Default score</th>
                    <th className="text-left font-medium px-3 py-2">Horizon</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.top.map((a) => (
                    <tr key={a.asset_id} className="border-b border-slate-100">
                      <td className="px-3 py-2 font-semibold tabular-nums">{a.new_rank}</td>
                      <td className="px-3 py-2 text-slate-500 tabular-nums">{a.old_rank ? Math.round(a.old_rank) : "—"}</td>
                      <td className="px-3 py-2 text-slate-800 max-w-[300px] truncate">{a.asset_name}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate">{a.site_name}</td>
                      <td className="px-3 py-2 text-right font-medium tabular-nums">{a.score}</td>
                      <td className="px-3 py-2 text-right text-slate-500 tabular-nums">{a.old_score ? Math.round(a.old_score) : "—"}</td>
                      <td className="px-3 py-2">
                        <Badge className={`text-[10px] hover:bg-inherit ${
                          a.status === "Immediate Attention" ? "bg-rose-100 text-rose-700"
                          : a.status === "12 Month Plan" ? "bg-amber-100 text-amber-700"
                          : a.status === "24 Month Plan" ? "bg-blue-100 text-blue-700"
                          : "bg-slate-100 text-slate-600"}`}>{a.status === "Immediate Attention" ? "Beyond Design Life" : a.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
